/**
 * The main-thread side of the atlas worker: a small pool, one request id per
 * cut, and a clean `null` where the platform has no workers or no
 * OffscreenCanvas (tests, old WebViews), so callers fall back to cutting on
 * the main thread.
 *
 * The worker is bundled as its own entry (`scripts/build-web.js`) and served
 * beside the app as `atlas.worker.js`.
 */
const POOL = Math.max(1, Math.min(3, (typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? navigator.hardwareConcurrency : 2) - 1));
const workers = [];
const waiting = new Map();
let nextId = 1;
let robin = 0;
let unavailable = false;

function supported() {
  return !unavailable
    && typeof Worker !== 'undefined'
    && typeof OffscreenCanvas !== 'undefined'
    && typeof document !== 'undefined';
}

function worker(i) {
  if (!workers[i]) {
    const w = new Worker(new URL('atlas.worker.js', document.baseURI), { type: 'module' });
    w.onmessage = (e) => {
      const { id, atlases, error } = e.data;
      const settle = waiting.get(id);
      if (!settle) return;
      waiting.delete(id);
      if (error) settle.reject(new Error(error));
      else settle.resolve(atlases);
    };
    w.onerror = (err) => {
      // A worker that will not start (a missing bundle, a CSP): everything
      // in flight falls back, and no more is sent its way.
      console.error('[dice] atlas worker failed', err.message || err);
      unavailable = true;
      for (const settle of waiting.values()) settle.resolve(null);
      waiting.clear();
    };
    workers[i] = w;
  }
  return workers[i];
}

/**
 * Cut a body's atlases off the main thread. Resolves to
 * `{ map, normal, roughness, foilArt, foilMode }`: flipped ImageBitmaps,
 * optional foil artwork and its numeric coverage classification,
 * or `null` if the cut has to happen on the main thread instead.
 */
export function cutAtlasesInWorker(job) {
  if (!supported()) return Promise.resolve(null);
  const id = nextId++;
  return new Promise((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    const { palette } = job;
    // Only what the painter reads; a palette may carry lab canvases, which cannot cross.
    const plain = {
      id: palette.id, fill: palette.fill, wear: palette.wear, cardFilter: palette.cardFilter,
      numeralFilter: palette.numeralFilter, numeralColor: palette.numeralColor, numeralWeight: palette.numeralWeight,
      foil: Boolean(palette.foil),
      tile: palette.tile, height: palette.height, roughness: palette.roughness,
    };
    worker(robin++ % POOL).postMessage({ id, ...job, palette: plain });
  }).catch((err) => {
    console.error('[dice] atlas cut failed in worker, cutting on the main thread', err);
    return null;
  });
}
